// src/utils/totalGuestsPerDayCsvUtils.js

/**
 * Convert an array of objects into a CSV string.
 * Uses the keys of the first object as headers.
 * @param {Object[]} objArray
 * @returns {string}
 */
export function convertToCSV(objArray) {
  if (!Array.isArray(objArray) || objArray.length === 0) return "";

  const headers = Object.keys(objArray[0]);
  const escape = (val) => {
    let str = val == null ? "" : String(val);
    if (str.includes('"')) str = str.replace(/"/g, '""');
    return `"${str}"`;
  };

  let csv = headers.map(escape).join(",") + "\r\n";

  objArray.forEach((row) => {
    const line = headers.map((key) => escape(row[key])).join(",");
    csv += line + "\r\n";
  });

  return csv;
}

/**
 * Export a CSV of total guests per day of week by transaction count.
 * Categories are determined by tx.cust_count:
 *  - 1 => Solo
 *  - 2 => Couple
 *  - 3-4 => Small Group
 *  - 5-7 => Medium Group
 *  - >=8 => Large Group
 *
 * @param {Array}  transactions  - filtered list of transactions with tran_date and cust_count
 * @param {string} storeName     - name of the store (e.g., 'All Stores')
 * @param {string} [fileName]    - CSV download filename
 */
export function exportTotalGuestsPerDayCSV(
  transactions,
  storeName,
  fileName = "TotalGuestsPerDay.csv"
) {
  const days = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const categories = [
    "Solo",
    "Couple",
    "Small Group",
    "Medium Group",
    "Large Group",
  ];

  // Initialize counts
  const dataByDay = {};
  days.forEach((day) => {
    dataByDay[day] = categories.reduce(
      (acc, cat) => ({ ...acc, [cat]: 0 }),
      {}
    );
  });

  // Tally transactions
  transactions.forEach((tx) => {
    const day = new Date(tx.tran_date).toLocaleString("default", {
      weekday: "long",
    });
    const count = parseInt(tx.cust_count, 10);
    if (!dataByDay[day]) return;

    if (count === 1) dataByDay[day].Solo++;
    else if (count === 2) dataByDay[day].Couple++;
    else if (count >= 3 && count <= 4) dataByDay[day]["Small Group"]++;
    else if (count >= 5 && count <= 7) dataByDay[day]["Medium Group"]++;
    else if (count >= 8) dataByDay[day]["Large Group"]++;
  });

  // Build rows
  const rows = days.map((day) => ({
    Day: day,
    Store: storeName,
    ...dataByDay[day],
  }));

  // Convert and download
  const csvString = convertToCSV(rows);
  if (!csvString) {
    console.warn("exportTotalGuestsPerDayCSV: no data to export");
    return;
  }

  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}
